
import { useState } from 'react';
import { useCustomer } from './hooks/useCustomer';
import { useForm } from 'react-hook-form';

export default function CustomerWarranty() {
  const { warranty } = useCustomer();
  const w = warranty();
  const [showClaimForm, setShowClaimForm] = useState(false);
  const { register, handleSubmit, reset } = useForm();

  if (w.isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        <span className="ml-2">Loading warranties...</span>
      </div>
    );
  }

  const activeWarranties = w.data?.filter((warranty: any) => 
    new Date(warranty.expiryDate) > new Date()
  ) || [];

  const expiredWarranties = w.data?.filter((warranty: any) => 
    new Date(warranty.expiryDate) <= new Date()
  ) || [];

  const onSubmitClaim = async (data: any) => {
    // Handle warranty claim submission
    console.log('Warranty claim:', data);
    setShowClaimForm(false);
    reset();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'claimed': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDaysRemaining = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-amber-50 to-orange-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Warranty Management</h1>
            <p className="text-gray-600">Track your product warranties and submit claims</p>
          </div>
          <button
            onClick={() => setShowClaimForm(true)}
            className="bg-amber-600 text-white px-6 py-2 rounded-lg hover:bg-amber-700 transition-colors"
          >
            <i className="ri-add-line mr-2"></i>
            New Claim
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border-t-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Active Warranties</h3>
              <p className="text-2xl font-bold text-green-600">{activeWarranties.length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-shield-check-fill text-green-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-t-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Expired Warranties</h3>
              <p className="text-2xl font-bold text-red-600">{expiredWarranties.length}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <i className="ri-shield-cross-fill text-red-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-t-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Total Claims</h3>
              <p className="text-2xl font-bold text-yellow-600">
                {w.data?.filter((w: any) => w.status === 'claimed').length || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <i className="ri-file-list-3-fill text-yellow-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Active Warranties */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Active Warranties</h2>
        </div>
        
        {activeWarranties.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Product</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Order No</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Purchase Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Warranty Period</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Days Remaining</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {activeWarranties.map((warranty: any) => {
                  const daysRemaining = getDaysRemaining(warranty.expiryDate);
                  return (
                    <tr key={warranty.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{warranty.product}</div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{warranty.orderNo}</td>
                      <td className="px-6 py-4 text-gray-600">{warranty.purchaseDate}</td>
                      <td className="px-6 py-4 text-gray-600">{warranty.warrantyPeriod} months</td>
                      <td className="px-6 py-4">
                        <span className={`font-medium ${
                          daysRemaining > 30 ? 'text-green-600' :
                          daysRemaining > 7 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {daysRemaining} days
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(warranty.status)}`}>
                          {warranty.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setShowClaimForm(true)}
                          className="text-amber-600 hover:text-amber-700 font-medium text-sm"
                        >
                          File Claim
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <i className="ri-shield-check-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No active warranties</h3>
            <p className="text-gray-500">Your warranty information will appear here once you make a purchase.</p>
          </div>
        )}
      </div>

      {/* Expired Warranties */}
      {expiredWarranties.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Expired Warranties</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Product</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Order No</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Expiry Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {expiredWarranties.map((warranty: any) => (
                  <tr key={warranty.id} className="hover:bg-gray-50 opacity-75">
                    <td className="px-6 py-4 font-medium text-gray-900">{warranty.product}</td>
                    <td className="px-6 py-4 text-gray-600">{warranty.orderNo}</td>
                    <td className="px-6 py-4 text-gray-600">{warranty.expiryDate}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Expired
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Warranty Claim Form Modal */}
      {showClaimForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800">Submit Warranty Claim</h2>
                <button
                  onClick={() => setShowClaimForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit(onSubmitClaim)} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order Number</label>
                <input
                  {...register('orderNo', { required: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Enter order number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
                <input
                  {...register('product', { required: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Enter product name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Issue Description</label>
                <textarea
                  {...register('issueDescription', { required: true })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Describe the issue with your product"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowClaimForm(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-amber-600 text-white py-2 px-4 rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Submit Claim
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
